import { create } from 'zustand';
import chatService from '../services/chatService';
import { getSocket } from '../config/socket';
import useAuthStore from './authStore';

const playNotificationSound = () => {
  try {
    const audio = new Audio('/sounds/notification.mp3');
    audio.volume = 0.5;
    audio.play().catch((err) => console.warn('Notification sound blocked:', err.message));
  } catch (err) {
    console.warn('Notification sound error:', err);
  }
};

const useChatStore = create((set, get) => ({
  channels: [],
  activeChannelId: null,
  activeChannel: null,
  messages: {},
  hasMoreMessages: {},
  channelMembers: {},
  typingUsers: {},
  onlineUsers: new Set(),
  unreadCounts: {},
  loading: false,
  messagesLoading: false,
  searchResults: null,

  // ── Channels ─────────────────────────────────────────
  fetchChannels: async () => {
    set({ loading: true });
    try {
      const response = await chatService.getChannels();
      const channels = response.data;
      const unreadCounts = {};
      for (const ch of channels) {
        unreadCounts[ch.id] = ch.unread_count || 0;
      }
      set({ channels, unreadCounts, loading: false });
    } catch (error) {
      console.error('Failed to fetch channels:', error);
      set({ loading: false });
    }
  },

  setActiveChannel: async (channelId) => {
    if (!channelId) {
      set({ activeChannelId: null, activeChannel: null });
      return;
    }
    set({ activeChannelId: channelId });

    try {
      const response = await chatService.getChannel(channelId);
      set({ activeChannel: response.data });

      // Fetch messages if not loaded
      if (!get().messages[channelId]) {
        await get().fetchMessages(channelId);
      }

      // Fetch members
      const membersRes = await chatService.getMembers(channelId);
      set((state) => ({
        channelMembers: { ...state.channelMembers, [channelId]: membersRes.data },
      }));

      // Join socket room
      const socket = getSocket();
      if (socket) {
        socket.emit('chat:join:channel', { channelId });
      }

      // Mark as read — read messages AFTER fetch has completed
      const loadedMsgs = get().messages[channelId];
      if (loadedMsgs && loadedMsgs.length > 0) {
        const lastMsg = loadedMsgs[loadedMsgs.length - 1];
        get().markAsRead(channelId, lastMsg.id);
      }
    } catch (error) {
      console.error('Failed to load channel:', error);
    }
  },

  createChannel: async (data) => {
    const response = await chatService.createChannel(data);
    await get().fetchChannels();
    return response.data;
  },

  createDirect: async (targetUserId) => {
    const response = await chatService.createDirect(targetUserId);
    await get().fetchChannels();
    return response.data;
  },

  updateChannel: async (channelId, data) => {
    await chatService.updateChannel(channelId, data);
    await get().fetchChannels();
    if (get().activeChannelId === channelId) {
      const response = await chatService.getChannel(channelId);
      set({ activeChannel: response.data });
    }
  },

  // ── Messages ─────────────────────────────────────────
  fetchMessages: async (channelId, before) => {
    set({ messagesLoading: true });
    try {
      const response = await chatService.getMessages(channelId, { before, limit: 50 });
      const { messages: msgData, hasMore } = response.data;

      set((state) => {
        const existing = before ? (state.messages[channelId] || []) : [];
        const newMessages = before ? [...msgData, ...existing] : msgData;
        return {
          messages: { ...state.messages, [channelId]: newMessages },
          hasMoreMessages: { ...state.hasMoreMessages, [channelId]: hasMore },
          messagesLoading: false,
        };
      });
    } catch (error) {
      console.error('Failed to fetch messages:', error);
      set({ messagesLoading: false });
    }
  },

  sendMessage: async (channelId, content, messageType = 'text', parentMessageId = null) => {
    const socket = getSocket();
    const organizationId = localStorage.getItem('organizationId');

    if (socket?.connected) {
      socket.emit('chat:message:send', {
        organizationId,
        channelId,
        content,
        messageType,
        parentMessageId,
      }, (response) => {
        if (!response?.success) {
          // Socket send failed — fall back to REST
          chatService.sendMessage(channelId, { content, messageType, parentMessageId })
            .then((res) => {
              const message = res.data;
              set((state) => {
                const existing = state.messages[channelId] || [];
                if (existing.some((m) => m.id === message.id)) return state;
                return { messages: { ...state.messages, [channelId]: [...existing, message] } };
              });
            })
            .catch(() => {});
        }
      });
    } else {
      // Socket not connected — use REST API directly
      try {
        const res = await chatService.sendMessage(channelId, { content, messageType, parentMessageId });
        const message = res.data;
        set((state) => {
          const existing = state.messages[channelId] || [];
          if (existing.some((m) => m.id === message.id)) return state;
          return { messages: { ...state.messages, [channelId]: [...existing, message] } };
        });
        get().fetchChannels();
      } catch (error) {
        console.error('Failed to send message:', error);
      }
    }
  },

  uploadFile: async (channelId, file, content) => {
    try {
      const response = await chatService.uploadFile(channelId, file, content);
      const message = response.data;

      // Add to local messages since REST upload doesn't go through socket broadcast
      set((state) => {
        const existing = state.messages[channelId] || [];
        if (existing.some((m) => m.id === message.id)) return state;
        return {
          messages: { ...state.messages, [channelId]: [...existing, message] },
        };
      });

      // Refresh channel list to update last message
      get().fetchChannels();

      return message;
    } catch (error) {
      console.error('Failed to upload file:', error);
      throw error;
    }
  },

  editMessage: (messageId, content) => {
    const socket = getSocket();
    if (!socket) return;
    socket.emit('chat:message:edit', { messageId, content });
  },

  deleteMessage: (messageId) => {
    const socket = getSocket();
    if (!socket) return;
    socket.emit('chat:message:delete', { messageId });
  },

  markAsRead: async (channelId, messageId) => {
    try {
      set((state) => ({
        unreadCounts: { ...state.unreadCounts, [channelId]: 0 },
      }));

      const socket = getSocket();
      if (socket?.connected) {
        socket.emit('chat:message:read', { channelId, messageId });
      }

      // REST fallback to guarantee persistence
      await chatService.markAsRead(channelId, messageId);
    } catch (error) {
      console.error('Failed to mark as read:', error);
    }
  },

  // ── Reactions ────────────────────────────────────────
  addReaction: (messageId, emoji, channelId) => {
    const socket = getSocket();
    if (!socket) return;
    socket.emit('chat:reaction:add', { messageId, emoji, channelId });
  },

  removeReaction: (messageId, emoji, channelId) => {
    const socket = getSocket();
    if (!socket) return;
    socket.emit('chat:reaction:remove', { messageId, emoji, channelId });
  },

  // ── Members ──────────────────────────────────────────
  addMembers: async (channelId, userIds) => {
    const response = await chatService.addMembers(channelId, userIds);
    set((state) => ({
      channelMembers: { ...state.channelMembers, [channelId]: response.data },
    }));
  },

  removeMember: async (channelId, userId) => {
    await chatService.removeMember(channelId, userId);
    const response = await chatService.getMembers(channelId);
    set((state) => ({
      channelMembers: { ...state.channelMembers, [channelId]: response.data },
    }));
  },

  // ── Typing ───────────────────────────────────────────
  startTyping: (channelId) => {
    const socket = getSocket();
    if (socket) socket.emit('chat:typing:start', { channelId });
  },

  stopTyping: (channelId) => {
    const socket = getSocket();
    if (socket) socket.emit('chat:typing:stop', { channelId });
  },

  // ── Search ───────────────────────────────────────────
  searchMessages: async (channelId, query) => {
    if (!query) {
      set({ searchResults: null });
      return;
    }
    try {
      const response = await chatService.searchMessages(channelId, query);
      set({ searchResults: response.data });
    } catch (error) {
      console.error('Failed to search:', error);
    }
  },

  clearSearch: () => set({ searchResults: null }),

  // ── Socket Initialization ────────────────────────────
  initChatSocket: () => {
    const socket = getSocket();
    if (!socket) return;

    // Join all channel rooms
    const joinAllChannels = () => {
      const { channels } = get();
      const channelIds = channels.map((c) => c.id);
      if (channelIds.length) {
        socket.emit('chat:join', { channelIds });
      }
      // Refresh channel list + messages for active channel on reconnect
      get().fetchChannels();
      const { activeChannelId } = get();
      if (activeChannelId) {
        get().fetchMessages(activeChannelId);
      }
    };

    joinAllChannels();

    // Register reconnect handler so rooms are re-joined after disconnect
    socket._onReconnect = joinAllChannels;

    // New message
    socket.on('chat:message:new', (message) => {
      set((state) => {
        const channelId = message.channel_id;
        const existing = state.messages[channelId] || [];
        // Avoid duplicates
        if (existing.some((m) => m.id === message.id)) return state;

        return {
          messages: { ...state.messages, [channelId]: [...existing, message] },
        };
      });

      // Play notification sound for messages from others
      const currentUser = useAuthStore.getState().user;
      if (message.sender_id !== currentUser?.id) {
        playNotificationSound();
      }

      // Update channel list order
      get().fetchChannels();

      // If in active channel, mark as read
      const { activeChannelId } = get();
      if (activeChannelId === message.channel_id) {
        get().markAsRead(message.channel_id, message.id);
      }
    });

    // Message updated
    socket.on('chat:message:updated', (message) => {
      set((state) => {
        const channelId = message.channel_id;
        const existing = state.messages[channelId] || [];
        return {
          messages: {
            ...state.messages,
            [channelId]: existing.map((m) => (m.id === message.id ? message : m)),
          },
        };
      });
    });

    // Message deleted
    socket.on('chat:message:deleted', ({ messageId, channelId }) => {
      set((state) => {
        const existing = state.messages[channelId] || [];
        return {
          messages: {
            ...state.messages,
            [channelId]: existing.filter((m) => m.id !== messageId),
          },
        };
      });
    });

    // Typing indicators
    socket.on('chat:typing', ({ channelId, userId, isTyping }) => {
      set((state) => {
        const current = state.typingUsers[channelId] || [];
        const updated = isTyping
          ? [...current.filter((u) => u !== userId), userId]
          : current.filter((u) => u !== userId);
        return {
          typingUsers: { ...state.typingUsers, [channelId]: updated },
        };
      });
    });

    // Reaction updates
    socket.on('chat:reaction:updated', ({ messageId, reactions }) => {
      set((state) => {
        const newMessages = { ...state.messages };
        for (const channelId of Object.keys(newMessages)) {
          newMessages[channelId] = newMessages[channelId].map((m) =>
            m.id === messageId ? { ...m, reactions } : m
          );
        }
        return { messages: newMessages };
      });
    });

    // Presence
    socket.on('chat:presence', ({ userId, status }) => {
      set((state) => {
        const onlineUsers = new Set(state.onlineUsers);
        if (status === 'online') {
          onlineUsers.add(userId);
        } else {
          onlineUsers.delete(userId);
        }
        return { onlineUsers };
      });
    });

    // Unread updates (when not in that channel)
    socket.on('chat:unread:update', ({ channelId }) => {
      const { activeChannelId } = get();
      if (activeChannelId !== channelId) {
        set((state) => ({
          unreadCounts: {
            ...state.unreadCounts,
            [channelId]: (state.unreadCounts[channelId] || 0) + 1,
          },
        }));
      }
    });
  },

  cleanupChatSocket: () => {
    const socket = getSocket();
    if (!socket) return;
    socket.off('chat:message:new');
    socket.off('chat:message:updated');
    socket.off('chat:message:deleted');
    socket.off('chat:typing');
    socket.off('chat:reaction:updated');
    socket.off('chat:presence');
    socket.off('chat:unread:update');
    socket._onReconnect = null;
  },

  // ── Computed ─────────────────────────────────────────
  getTotalUnread: () => {
    const { unreadCounts } = get();
    return Object.values(unreadCounts).reduce((sum, count) => sum + count, 0);
  },
}));

export default useChatStore;
